// src/components/common/ReportDialog.tsx
// Dialog de signalement de posts/commentaires
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  FormLabel,
} from '@mui/material';
import ReportIcon from '@mui/icons-material/Report';

export type ReportReason =
  | 'spam'
  | 'inappropriate'
  | 'harassment'
  | 'misinformation'
  | 'hate_speech'
  | 'violence'
  | 'copyright'
  | 'other';

export interface ReportReasonConfig {
  value: ReportReason;
  label: string;
  description: string;
}

export const REPORT_REASONS: ReportReasonConfig[] = [
  {
    value: 'spam',
    label: 'Spam',
    description: 'Contenu répétitif ou promotionnel non sollicité',
  },
  {
    value: 'inappropriate',
    label: 'Contenu inapproprié',
    description: 'Contenu ne respectant pas les valeurs de la communauté',
  },
  {
    value: 'harassment',
    label: 'Harcèlement',
    description: 'Comportement abusif ou intimidant envers quelqu\'un',
  },
  {
    value: 'misinformation',
    label: 'Désinformation',
    description: 'Informations fausses ou trompeuses',
  },
  {
    value: 'hate_speech',
    label: 'Discours haineux',
    description: 'Contenu discriminatoire ou incitant à la haine',
  },
  {
    value: 'violence',
    label: 'Violence',
    description: 'Contenu violent ou incitant à la violence',
  },
  {
    value: 'copyright',
    label: 'Violation de droits d\'auteur',
    description: 'Contenu utilisant du matériel protégé sans autorisation',
  },
  {
    value: 'other',
    label: 'Autre',
    description: 'Autre raison non listée ci-dessus',
  },
];

interface ReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: ReportReason, details?: string) => Promise<void>;
  targetType: 'post' | 'comment' | 'user';
  targetId: number;
}

const ReportDialog: React.FC<ReportDialogProps> = ({
  open,
  onClose,
  onSubmit,
  targetType,
  targetId,
}) => {
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(selectedReason, details.trim() || undefined);
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue lors du signalement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setSelectedReason(null);
    setDetails('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  const getTargetLabel = () => {
    switch (targetType) {
      case 'post':
        return 'ce post';
      case 'comment':
        return 'ce commentaire';
      case 'user':
        return 'cet utilisateur';
      default:
        return 'ce contenu';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReportIcon color="error" />
        <Typography variant="h6">Signaler {getTargetLabel()}</Typography>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Merci pour votre signalement. Notre équipe va l'examiner.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Aidez-nous à comprendre le problème. Sélectionnez la raison qui décrit le mieux
              pourquoi vous signalez {getTargetLabel()}.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Raison du signalement
              </FormLabel>
              <RadioGroup
                value={selectedReason || ''}
                onChange={(e) => setSelectedReason(e.target.value as ReportReason)}
              >
                {REPORT_REASONS.map((reason) => (
                  <FormControlLabel
                    key={reason.value}
                    value={reason.value}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {reason.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {reason.description}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      alignItems: 'flex-start',
                      mb: 1,
                      p: 1,
                      borderRadius: 2,
                      mx: 0,
                      bgcolor: selectedReason === reason.value ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {selectedReason === 'other' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Détails supplémentaires"
                placeholder="Veuillez décrire le problème..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                sx={{ mt: 2 }}
                required
              />
            )}

            {selectedReason && selectedReason !== 'other' && (
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Détails supplémentaires (optionnel)"
                placeholder="Informations complémentaires..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmit}
            disabled={
              !selectedReason ||
              isSubmitting ||
              (selectedReason === 'other' && !details.trim())
            }
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <ReportIcon />}
          >
            {isSubmitting ? 'Envoi...' : 'Signaler'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ReportDialog;
